import {
  STORAGE_KEY,
  parseJSONFromSelector,
  haversine,
  formatWorkHours,
  geocodeForMarket,
  isCountryAllowed,
  debounce,
  isCcStore,
} from '@cc/utils';
import { ensureCoords, getCoords, subscribe as subscribeGeo } from '@cc/geolocation';

if (!customElements.get('cc-store-selector')) {
  customElements.define(
    'cc-store-selector',
    class StoreSelector extends HTMLElement {
      static COOKIE_KEY = 'zippy_selected_store';
      static CC_FILTER_PARAM = 'filter.p.m.custom.in_stock_locations';
      // Live search while typing: local text matches update on every
      // keystroke, the geocode request only fires once typing settles and
      // the query is long enough to be meaningful — keeps /apps/proxy
      // traffic close to the old Enter-only volume.
      static LIVE_SEARCH_DELAY_MS = 350;
      static LIVE_GEOCODE_MIN_LENGTH = 3;
      static AUTO_CLOSE_DELAY = 3000;

      constructor() {
        super();

        this.stores = [];
        this.allowedCountries = [];
        this.countryAllowed = true;
        this.i18n = {};
        this.selectedStoreId = null;
        this.hasSeenPrompt = false;
        this.query = '';
        this.autoCloseTimer = null;
        this.pendingStore = null;
        this.abortController = null;
        this.selectedStoreGid = null;
        this.warehouseGid = null;
        this._unsubscribeGeo = null;
        this._searchToken = 0;
      }

      get userLat() {
        return getCoords()?.lat ?? null;
      }

      get userLng() {
        return getCoords()?.lng ?? null;
      }

      get refs() {
        if (this._refs) return this._refs;

        this._refs = {
          backdrop: this.querySelector('[ref="backdrop"]'),
          panel: this.querySelector('[ref="panel"]'),
          closeButton: this.querySelector('[ref="closeButton"]'),
          searchInput: this.querySelector('[ref="searchInput"]'),
          searchButton: this.querySelector('[ref="searchButton"]'),
          geoButton: this.querySelector('[ref="geoButton"]'),
          geoIconDefault: this.querySelector('[ref="geoIconDefault"]'),
          geoIconActive: this.querySelector('[ref="geoIconActive"]'),
          geoButtonLoader: this.querySelector('[ref="geoButtonLoader"]'),
          geoLoader: this.querySelector('[ref="geoLoader"]'),
          results: this.querySelector('[ref="results"]'),
          listView: this.querySelector('[ref="listView"]'),
          empty: this.querySelector('[ref="empty"]'),
          confirmation: this.querySelector('[ref="confirmation"]'),
          confirmationStore: this.querySelector('[ref="confirmationStore"]'),
          confirmationClose: this.querySelector('[ref="confirmationClose"]'),
          changeDialog: this.querySelector('[ref="changeDialog"]'),
          changeCurrentStore: this.querySelector('[ref="changeCurrentStore"]'),
          changeClose: this.querySelector('[ref="changeClose"]'),
          changeKeep: this.querySelector('[ref="changeKeep"]'),
          changeConfirm: this.querySelector('[ref="changeConfirm"]'),
          storeCard: this.querySelector('template[ref="storeCard"]'),
        };

        return this._refs;
      }

      connectedCallback() {
        this.abortController = new AbortController();

        const data = this.parseJSON('[data-cc-store-selector-data]') || {};
        // Status on and local pickup on — this list also drives the nearby
        // stores, the collection filter and the saved-store validation.
        this.stores = (data.stores || []).filter(isCcStore);
        this.allowedCountries = (data.settings?.allowed_countries || '')
          .split(',')
          .map((c) => c.trim().toUpperCase())
          .filter(Boolean);
        this.currentCountry = (data.settings?.current_country || '').toUpperCase();
        this.currentCountryName = data.settings?.current_country_name || '';
        this.radiusKm = Number(data.settings?.radius_km) || 0;
        this.warehouseGid = data.settings?.warehouse_gid || null;

        // `location_id` on each metaobject store is the full Shopify location
        // GID (gid://shopify/Location/...). It is compared directly against
        // `warehouseGid` (also a full GID), and used as the canonical store
        // identifier persisted in localStorage as selectedStoreGid.

        this.i18n = this.parseJSON('[data-cc-store-selector-i18n]') || {};

        this.triggers = document.querySelectorAll('[data-cc-store-selector-open]');
        this.triggerLabels = document.querySelectorAll('[data-cc-store-selector-label]');
        this.triggerEyebrows = document.querySelectorAll('[data-cc-store-selector-eyebrow]');
        this.triggerChangeLinks = document.querySelectorAll('[data-cc-store-selector-change]');
        this.triggerRemoveLinks = document.querySelectorAll('[data-cc-store-selector-remove]');
        this.triggerStates = document.querySelectorAll('[data-cc-store-selector-state]');

        // Synchronous country pre-check — currentCountry is server-rendered so
        // this is instant. Gates the URL-manipulating methods below so that
        // refreshNearbyGids() / validateState() never fire a location.replace()
        // on markets where CC is not active. Without this, those methods redirect
        // to warehouse-only while the head script simultaneously redirects back
        // to the full GID set, creating an infinite reload loop.
        if (this.allowedCountries.length > 0 && this.currentCountry) {
          this.countryAllowed = this.allowedCountries.includes(this.currentCountry);
        }

        this.hydrateState();

        if (this.countryAllowed) {
          this.refreshNearbyGids();
          this.validateState();
        }

        const restoredStore = this.getSelectedStore();
        if (restoredStore) this._syncCartAttributes(restoredStore);

        this.bindEvents();
        this.updateResults([]);
        this.subscribeToGeo();

        if (this.allowedCountries.length > 0) {
          this.detectCountryAndInit();
        } else {
          this.countryAllowed = true;
          this.toggleTriggerVisibility(false);
          this.updateTriggers();
          this.autoOpenIfNeeded();
        }
      }

      async detectCountryAndInit() {
        if (this.currentCountry) {
          // Server-rendered market country — instant, no API call needed
          this.countryAllowed = this.allowedCountries.includes(this.currentCountry);
        } else {
          this.countryAllowed = await isCountryAllowed(
            this.allowedCountries,
            this.abortController.signal,
          );
        }

        this.toggleTriggerVisibility(!this.countryAllowed);
        this.updateTriggers();
        this.autoOpenIfNeeded();
      }

      async autoOpenIfNeeded() {
        if (!this.countryAllowed) return;
        if (this.hasSeenPrompt || this.selectedStoreId) return;

        await this._waitForCookieConsent();
        window.setTimeout(() => this.openPanel(), 250);
      }

      _waitForCookieConsent() {
        return new Promise((resolve) => {
          // OneTrust already resolved
          if (window.OnetrustActiveGroups) {
            resolve();
            return;
          }
          // No OneTrust on page — proceed immediately
          if (!document.querySelector('[id^="onetrust"]')) {
            resolve();
            return;
          }
          const timeout = window.setTimeout(resolve, 5000);
          document.addEventListener('OneTrustGroupsUpdated', () => {
            window.clearTimeout(timeout);
            resolve();
          }, { once: true });
        });
      }

      toggleTriggerVisibility(hide) {
        this.triggers.forEach((trigger) => {
          trigger.closest('.cc-store-selector-trigger')?.classList.toggle('hidden', hide);
        });

        if (this.refs.panel) {
          this.refs.panel.classList.toggle('hidden', hide);
        }
      }

      disconnectedCallback() {
        this.abortController?.abort();
        this._debouncedLiveSearch?.cancel();

        if (this.autoCloseTimer) {
          window.clearTimeout(this.autoCloseTimer);
          this.autoCloseTimer = null;
        }

        this._unsubscribeGeo?.();
        this._unsubscribeGeo = null;
      }

      parseJSON(selector) {
        return parseJSONFromSelector(this, selector);
      }

      hydrateState() {
        try {
          const raw = window.localStorage.getItem(STORAGE_KEY);
          if (!raw) return;

          const parsed = JSON.parse(raw);
          this.selectedStoreId = parsed.selectedStoreId || null;
          this.selectedStoreGid = parsed.selectedStoreGid || null;
          this.nearbyStoreGids = Array.isArray(parsed.nearbyStoreGids) ? parsed.nearbyStoreGids : [];
          this.warehouseGid = parsed.warehouseGid || this.warehouseGid || null;
          this.hasSeenPrompt = Boolean(parsed.hasSeenPrompt);
        } catch {
          // Storage unavailable or corrupt — start fresh
        }
      }

      refreshNearbyGids() {
        const store = this.getSelectedStore();

        if (!store) {
          // No store selected — clean up stale filter params to warehouse-only
          const params = new URLSearchParams(window.location.search);
          if (params.has(StoreSelector.CC_FILTER_PARAM)) {
            const currentGids = new Set(params.getAll(StoreSelector.CC_FILTER_PARAM));
            const isClean = this.warehouseGid
              ? currentGids.size === 1 && currentGids.has(this.warehouseGid)
              : currentGids.size === 0;

            if (!isClean) {
              params.delete(StoreSelector.CC_FILTER_PARAM);
              if (this.warehouseGid) {
                params.append(StoreSelector.CC_FILTER_PARAM, this.warehouseGid);
              }
              const qs = params.toString();
              window.location.replace(`${window.location.pathname}${qs ? `?${qs}` : ''}`);
            }
          }
          return;
        }

        // Always recompute with current radiusKm and persist
        const freshGids = this.computeNearbyGids(store);
        this.nearbyStoreGids = freshGids;
        this.persistState();

        // Build the expected set of filter GIDs
        const expectedGids = new Set();
        if (this.warehouseGid) expectedGids.add(this.warehouseGid);
        if (this.selectedStoreGid) expectedGids.add(this.selectedStoreGid);
        freshGids.forEach((g) => expectedGids.add(g));

        // Compare URL params against expected GIDs — redirect only if stale
        const params = new URLSearchParams(window.location.search);
        if (params.has(StoreSelector.CC_FILTER_PARAM)) {
          const currentGids = new Set(params.getAll(StoreSelector.CC_FILTER_PARAM));
          const isStale =
            expectedGids.size !== currentGids.size ||
            [...expectedGids].some((g) => !currentGids.has(g));

          if (isStale) {
            params.delete(StoreSelector.CC_FILTER_PARAM);
            for (const gid of expectedGids) {
              params.append(StoreSelector.CC_FILTER_PARAM, gid);
            }
            const qs = params.toString();
            window.location.replace(`${window.location.pathname}${qs ? `?${qs}` : ''}`);
          }
        }
      }

      validateState() {
        const noStores = this.stores.length === 0;
        const storeGone = this.selectedStoreId && !this.getSelectedStore();

        if (noStores || storeGone) {
          this.selectedStoreId = null;
          this.selectedStoreGid = null;
          this.persistState();

          const params = new URLSearchParams(window.location.search);
          if (params.has(StoreSelector.CC_FILTER_PARAM)) {
            params.delete(StoreSelector.CC_FILTER_PARAM);
            if (this.warehouseGid) {
              params.append(StoreSelector.CC_FILTER_PARAM, this.warehouseGid);
            }
            const qs = params.toString();
            window.location.replace(`${window.location.pathname}${qs ? `?${qs}` : ''}`);
          }
        }
      }

      persistState() {
        const payload = {
          selectedStoreId: this.selectedStoreId,
          selectedStoreGid: this.selectedStoreGid,
          nearbyStoreGids: this.nearbyStoreGids || [],
          warehouseGid: this.warehouseGid || null,
          hasSeenPrompt: this.hasSeenPrompt,
        };

        try {
          window.localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
        } catch {
          // Storage full or blocked
        }

        if (this.selectedStoreId) {
          document.cookie = `${StoreSelector.COOKIE_KEY}=${encodeURIComponent(this.selectedStoreId)}; path=/; max-age=${60 * 60 * 24 * 365}; SameSite=Lax; Secure`;
        } else {
          document.cookie = `${StoreSelector.COOKIE_KEY}=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT; SameSite=Lax; Secure`;
        }
      }

      bindEvents() {
        const { signal } = this.abortController;

        document.addEventListener('click', (event) => {
          const trigger = event.target.closest('[data-cc-store-selector-open]');
          if (!trigger) return;

          // Remove/Change links are nested inside the trigger button — skip panel open
          if (event.target.closest('[data-cc-store-selector-remove]') ||
              event.target.closest('[data-cc-store-selector-change]')) return;

          event.preventDefault();

          const storeName = trigger.dataset.storeName || '';
          if (storeName && this.refs.searchInput) {
            this.refs.searchInput.value = storeName;
            this.query = storeName;
          }

          this.openPanel();

          if (storeName) {
            this.performSearch();
          }
        }, { signal });

        this.refs.closeButton?.addEventListener('click', () => this.handleDismiss(), { signal });
        this.refs.backdrop?.addEventListener('click', () => {
          if (this.refs.changeDialog && !this.refs.changeDialog.hidden) {
            this.hideChangeDialog();
          } else if (this.refs.confirmation && !this.refs.confirmation.hidden) {
            this.hideConfirmation();
          } else {
            this.handleDismiss();
          }
        }, { signal });
        this.refs.confirmationClose?.addEventListener('click', () => this.hideConfirmation(), { signal });

        document.addEventListener('keydown', (event) => {
          if (event.key === 'Escape') {
            if (this.refs.changeDialog && !this.refs.changeDialog.hidden) {
              this.hideChangeDialog();
            } else if (this.refs.confirmation && !this.refs.confirmation.hidden) {
              this.hideConfirmation();
            } else if (this.classList.contains('is-open')) {
              this.closePanel();
            }
            return;
          }

          if (event.key === 'Tab' && this.classList.contains('is-open') && this.refs.panel) {
            this.trapFocus(event);
          }
        }, { signal });

        this._debouncedLiveSearch = debounce(
          () => this.performSearch({ live: true }),
          StoreSelector.LIVE_SEARCH_DELAY_MS,
        );

        this.refs.searchInput?.addEventListener('input', (event) => {
          this.query = event.target.value.trim();
          if (!this.query) {
            this._debouncedLiveSearch.cancel();
            this.performSearch();
            return;
          }
          // Local text matches are free — show them on every keystroke.
          // Bumping the token drops any geocode still in flight for the
          // previous, shorter query so it can't overwrite these results.
          ++this._searchToken;
          this._resetDistances();
          const localMatches = this.filterStores(this.query);
          if (localMatches.length > 0) this.updateResults(localMatches);
          this._debouncedLiveSearch();
        }, { signal });

        this.refs.searchInput?.addEventListener('keydown', (event) => {
          if (event.key === 'Enter') {
            event.preventDefault();
            this.performSearch();
          }
        }, { signal });

        this.refs.searchButton?.addEventListener('click', () => this.performSearch(), { signal });
        this.refs.geoButton?.addEventListener('click', () => this.useGeolocation(), { signal });

        this.refs.changeClose?.addEventListener('click', () => {
          this.hideChangeDialog();
          this.closePanel();
        }, { signal });
        this.refs.changeKeep?.addEventListener('click', () => {
          this.hideChangeDialog();
          this.closePanel();
        }, { signal });
        this.refs.changeConfirm?.addEventListener('click', () => this.confirmChangeStore(), { signal });



        this.refs.results?.addEventListener('click', (event) => {
          const button = event.target.closest('[data-select-store]');
          if (button) this.selectStore(button.dataset.selectStore);
        }, { signal });

        document.addEventListener('click', (event) => {
          const changeLink = event.target.closest('[data-cc-store-selector-change]');
          if (!changeLink) return;
          event.preventDefault();
          event.stopPropagation();
          this.openPanel({ changeMode: true });
        }, { signal });

        document.addEventListener('click', (event) => {
          const removeLink = event.target.closest('[data-cc-store-selector-remove]');
          if (!removeLink) return;
          event.preventDefault();
          event.stopPropagation();
          this.removeStore();
        }, { signal });
      }

      handleDismiss() {
        this.hasSeenPrompt = true;
        this.persistState();
        this.closePanel();
        this.updateTriggers();
      }

      openPanel({ changeMode = false } = {}) {
        const { backdrop, panel, confirmation, searchInput } = this.refs;
        if (!backdrop || !panel) return;

        this.hasSeenPrompt = true;
        this.persistState();

        this._previouslyFocused = document.activeElement;

        this.classList.add('is-open');
        document.body.classList.add('cc-store-selector-open');

        if (confirmation) confirmation.hidden = true;

        if (changeMode) {
          // Reset query and show all stores sorted by proximity, selected store first.
          this.query = '';
          if (searchInput) searchInput.value = '';
          this._resetDistances();
          const allSorted = this._sortedAllStores();
          const idx = allSorted.findIndex((s) => s.location_id === this.selectedStoreId);
          if (idx > 0) {
            const [sel] = allSorted.splice(idx, 1);
            allSorted.unshift(sel);
          }
          this.updateResults(allSorted);
        } else if (!this.query) {
          // Re-opening with no query: refresh the list so it reflects the
          // current selection state (e.g. a store removed since last render
          // must no longer show as disabled). Selected store first, if any.
          // Km left over from an earlier search would no longer match the
          // empty field, nor the order.
          this._resetDistances();
          const allSorted = this._sortedAllStores();
          const idx = allSorted.findIndex((s) => s.location_id === this.selectedStoreId);
          if (idx > 0) {
            const [sel] = allSorted.splice(idx, 1);
            allSorted.unshift(sel);
          }
          this.updateResults(allSorted);
        }

        requestAnimationFrame(() => {
          (this.refs.closeButton || searchInput)?.focus();
        });
      }

      closePanel() {
        this.classList.remove('is-open', 'is-dialog');
        document.body.classList.remove('cc-store-selector-open');
        this._previouslyFocused?.focus();
        this._previouslyFocused = null;
      }

      trapFocus(event) {
        const focusable = [...this.refs.panel.querySelectorAll(
          'button:not([hidden]):not([disabled]), [href]:not([hidden]), input:not([hidden]):not([disabled]), select:not([hidden]):not([disabled]), textarea:not([hidden]):not([disabled]), [tabindex]:not([tabindex="-1"]):not([hidden])',
        )].filter((el) => el.offsetParent !== null);
        if (focusable.length === 0) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      }

      /**
       * @param {{live?: boolean}} [options] - `live` when triggered by typing
       *   (debounced) rather than an explicit Enter / search-button submit.
       */
      async performSearch({ live = false } = {}) {
        // An explicit submit supersedes a pending live search.
        this._debouncedLiveSearch?.cancel();

        // Each call invalidates any still-in-flight previous search. Async
        // results are only applied to the DOM if this token is still the
        // latest one by the time they resolve — otherwise an older, slower
        // request could resolve after a newer one and clobber its results.
        const searchToken = ++this._searchToken;
        const hasVisibleResults = Boolean(
          this.refs.results && !this.refs.results.hidden && this.refs.listView?.childElementCount,
        );
        this.showGeoLoader(false, { keepResults: live && hasVisibleResults });

        // Distances from the previous query's point must not leak into this
        // one's instant local matches (or a failed geocode's fallback list).
        this._resetDistances();

        if (!this.query) {
          this.updateResults(this._sortedAllStores());
          return;
        }

        const localMatches = this.filterStores(this.query);

        // Scope the geocode to the visitor's market (region bias + postal
        // code disambiguation) — see geocodeForMarket().
        const market = {
          countryCode: this.currentCountry,
          countryName: this.currentCountryName,
          stores: this.stores,
        };

        if (localMatches.length > 0) {
          // Show local matches immediately (fast), then geocode in the background
          // and replace them with ALL stores sorted by distance — the same list a
          // query without a local match gets, so "4760" and "4760-012", or
          // "Porto" and "Trofa", behave alike. The local matches only stay on
          // screen if the geocode fails.
          this.updateResults(localMatches);

          const coords = await geocodeForMarket(this.query, market);
          if (searchToken !== this._searchToken) return;
          if (coords) {
            this.computeDistancesFrom(coords.lat, coords.lng);
            this.updateResults(this._sortedByDistance());
          }
          return;
        }

        // While typing, a one- or two-character query with no local match is
        // too vague to geocode — keep whatever is on screen until the
        // customer types more (or submits explicitly).
        if (live && this.query.length < StoreSelector.LIVE_GEOCODE_MIN_LENGTH) return;

        // No local match — geocode the query and show ALL stores sorted by distance.
        // During live typing keep the current list on screen while the
        // geocode runs (spinner only), so the results don't flicker on every
        // pause in typing.
        this.showGeoLoader(true, { keepResults: live && hasVisibleResults });

        const coords = await geocodeForMarket(this.query, market);
        if (searchToken !== this._searchToken) return;

        if (coords) {
          this.computeDistancesFrom(coords.lat, coords.lng);
          this.showGeoLoader(false);
          this.updateResults(this._sortedByDistance());
        } else {
          this.showGeoLoader(false);
          this.updateResults([]);
        }
      }

      async useGeolocation() {
        this.showGeoLoader(true);

        const coords = await ensureCoords({ prompt: true });

        if (!coords) {
          this.showGeoLoader(false);
          this.setGeoIconActive(false);
          return;
        }

        this.computeDistances();

        this.query = this.i18n.geo_current_location;
        if (this.refs.searchInput) {
          this.refs.searchInput.value = this.query;
        }

        this.showGeoLoader(false);
        this.setGeoIconActive(true);
        this.updateResults(this._sortedByDistance());
      }

      /**
       * @param {boolean} show
       * @param {{keepResults?: boolean}} [options] - leave the results list
       *   and search button as they are, only toggle the spinner icon
       */
      showGeoLoader(show, { keepResults = false } = {}) {
        if (!keepResults) {
          if (this.refs.geoLoader) this.refs.geoLoader.hidden = !show;
          if (this.refs.searchButton) this.refs.searchButton.hidden = show;
          if (this.refs.results) this.refs.results.hidden = show;
          if (this.refs.empty) this.refs.empty.hidden = true;
        }

        if (this.refs.geoIconDefault) this.refs.geoIconDefault.classList.toggle('hidden', show);
        if (this.refs.geoIconActive) this.refs.geoIconActive.classList.add('hidden');
        if (this.refs.geoButtonLoader) this.refs.geoButtonLoader.classList.toggle('hidden', !show);
      }

      subscribeToGeo() {
        this._unsubscribeGeo = subscribeGeo(({ lat, lng }) => {
          const hasCoords = lat !== null && lng !== null;
          this.setGeoIconActive(hasCoords);
          if (hasCoords) {
            this.computeDistances();
          }
        });
      }

      setGeoIconActive(active) {
        if (this.refs.geoIconDefault) {
          this.refs.geoIconDefault.classList.toggle('hidden', active);
        }
        if (this.refs.geoIconActive) {
          this.refs.geoIconActive.classList.toggle('hidden', !active);
        }
        if (this.refs.geoButtonLoader) {
          this.refs.geoButtonLoader.classList.add('hidden');
        }
      }

      computeDistancesFrom(lat, lng) {
        for (const store of this.stores) {
          const sLat = Number.isFinite(+store.latitude) ? +store.latitude : null;
          const sLng = Number.isFinite(+store.longitude) ? +store.longitude : null;

          if (sLat !== null && sLng !== null) {
            store._distanceKm = haversine(lat, lng, sLat, sLng);
            store.distance = store._distanceKm < 10
              ? `${store._distanceKm.toFixed(1)} km`
              : `${Math.round(store._distanceKm)} km`;
          } else {
            store._distanceKm = null;
            store.distance = '';
          }
        }
      }

      computeDistances() {
        if (this.userLat === null || this.userLng === null) return;
        this.computeDistancesFrom(this.userLat, this.userLng);
      }

      /** Back to distances from the visitor's own location, or none. */
      _resetDistances() {
        if (this.userLat !== null && this.userLng !== null) {
          this.computeDistances();
          return;
        }
        for (const store of this.stores) {
          store._distanceKm = null;
          store.distance = '';
        }
      }

      _sortedByDistance() {
        return [...this.stores].sort(
          (a, b) => (a._distanceKm ?? Infinity) - (b._distanceKm ?? Infinity),
        );
      }

      _sortedAllStores() {
        const all = [...this.stores];
        if (this.userLat !== null) {
          all.sort((a, b) => (a._distanceKm ?? Infinity) - (b._distanceKm ?? Infinity));
        } else {
          all.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
        }
        return all;
      }

      updateResults(filtered) {
        const all = Array.isArray(filtered) ? filtered : [];
        const results = all.filter((s) => !this._isWarehouse(s));
        const hasResults = results.length > 0;

        this.renderList(results);

        if (this.refs.results) this.refs.results.hidden = !hasResults;
        if (this.refs.searchButton) this.refs.searchButton.hidden = hasResults;
        if (this.refs.empty) this.refs.empty.hidden = !(Boolean(this.query) && !hasResults);
      }

      renderList(results) {
        const listView = this.refs.listView;
        const template = this.refs.storeCard;
        if (!listView || !template) return;

        listView.innerHTML = '';

        for (const store of results) {
          const card = template.content.cloneNode(true);

          card.querySelector('[data-name]').textContent = store.name;
          card.querySelector('[data-address]').textContent = `${store.address1}${store.address2 ? ', ' + store.address2 : ''}${store.zip ? ', ' + store.zip : ''}`;
          const selectBtn = card.querySelector('[data-select-store]');
          selectBtn.dataset.selectStore = store.location_id;
          if (store.location_id === this.selectedStoreId) selectBtn.hidden = true;

          const distEl = card.querySelector('[data-distance]');
          const dist = store.distance || '';
          distEl.textContent = dist;
          if (!dist) distEl.hidden = true;

          const hoursRow = card.querySelector('[data-hours-row]');
          const formattedHours = formatWorkHours(store.work_hours, this.i18n.work_hours);
          if (formattedHours.length > 0) {
            const hoursEl = card.querySelector('[data-hours]');
            formattedHours.forEach((h, i) => {
              if (i > 0) hoursEl.appendChild(document.createElement('br'));
              hoursEl.appendChild(document.createTextNode(h));
            });
          } else if (hoursRow) {
            hoursRow.hidden = true;
          }

          listView.appendChild(card);
        }
      }



      filterStores(query) {
        if (!query) return [];
        const q = query.toLowerCase();
        const filtered = this.stores.filter((store) =>
          [store.name, store.city, store.zip, store.province, store.address1, store.address2]
            .filter(Boolean)
            .some((v) => v.toLowerCase().includes(q)),
        );

        if (this.userLat !== null) {
          filtered.sort((a, b) => (a._distanceKm ?? Infinity) - (b._distanceKm ?? Infinity));
        }

        return filtered;
      }

      selectStore(storeId) {
        const store = this.stores.find((s) => s.location_id === storeId);
        if (!store) return;

        if (this.selectedStoreId && this.selectedStoreId !== store.location_id) {
          this.pendingStore = store;
          this.showChangeDialog(store);
          return;
        }

        this.applyStoreSelection(store);
      }

      applyStoreSelection(store) {
        this.selectedStoreId = store.location_id;
        this.selectedStoreGid = store.location_id || null;
        this.nearbyStoreGids = this.computeNearbyGids(store);
        this.hasSeenPrompt = true;
        this.pendingStore = null;
        this.persistState();
        this.updateTriggers();
        this._syncCartAttributes(store);
        this.showConfirmation(store);

        window.dispatchEvent(
          new CustomEvent('zippy:cc-store-selector:change', {
            detail: { store, storeGid: store.location_id },
          }),
        );
      }

      showChangeDialog(newStore) {
        const { changeDialog, changeCurrentStore } = this.refs;
        if (!changeDialog) {
          this.applyStoreSelection(newStore);
          return;
        }

        const currentStore = this.getSelectedStore();
        if (changeCurrentStore && currentStore) {
          changeCurrentStore.textContent = currentStore.name.replace(/^Zippy\s*/i, '');
        }

        this.setDialogMode('change');
        this.classList.add('is-dialog');
        changeDialog.hidden = false;
      }

      setDialogMode(mode) {
        const dialog = this.refs.changeDialog;
        if (!dialog) return;
        const isRemove = mode === 'remove';
        dialog.querySelectorAll('[data-change-text]').forEach((el) => el.classList.toggle('hidden', isRemove));
        dialog.querySelectorAll('[data-remove-text]').forEach((el) => el.classList.toggle('hidden', !isRemove));
      }

      hideChangeDialog() {
        this.pendingStore = null;
        this._pendingRemove = false;

        if (this.refs.changeDialog) this.refs.changeDialog.hidden = true;

        this.classList.remove('is-dialog');
      }

      confirmChangeStore() {
        if (this.refs.changeDialog) this.refs.changeDialog.hidden = true;

        this.classList.remove('is-dialog');

        if (this._pendingRemove) {
          this._pendingRemove = false;
          this.applyStoreRemoval();
        } else if (this.pendingStore) {
          this.applyStoreSelection(this.pendingStore);
        }
      }

      removeStore() {
        if (!this.selectedStoreId) return;

        const { changeDialog, changeCurrentStore } = this.refs;
        if (!changeDialog) {
          this.applyStoreRemoval();
          return;
        }

        const currentStore = this.getSelectedStore();
        if (changeCurrentStore && currentStore) {
          changeCurrentStore.textContent = currentStore.name.replace(/^Zippy\s*/i, '');
        }

        this._pendingRemove = true;
        this.pendingStore = null;
        this.setDialogMode('remove');
        this.classList.add('is-dialog');
        changeDialog.hidden = false;
      }

      applyStoreRemoval() {
        this.selectedStoreId = null;
        this.selectedStoreGid = null;
        this.nearbyStoreGids = [];
        this.persistState();
        this.updateTriggers();
        this._clearCartAttributes();

        this.classList.remove('is-open', 'is-dialog');
        document.body.classList.remove('cc-store-selector-open');

        window.dispatchEvent(
          new CustomEvent('zippy:cc-store-selector:change', {
            detail: { store: null, storeGid: null },
          }),
        );

        // Redirect collection/search pages to apply warehouse-only filter
        const params = new URLSearchParams(window.location.search);
        const isFilterablePage = /^\/(collections|search)(\/|$|\?)/.test(window.location.pathname);

        if (params.has(StoreSelector.CC_FILTER_PARAM) || isFilterablePage) {
          params.delete(StoreSelector.CC_FILTER_PARAM);
          if (this.warehouseGid) {
            params.append(StoreSelector.CC_FILTER_PARAM, this.warehouseGid);
          }
          const qs = params.toString();
          window.location.replace(`${window.location.pathname}${qs ? `?${qs}` : ''}`);
        }
      }

      _syncCartAttributes(store) {
        const address = [
          store.address1,
          store.address2,
          [store.zip, store.city].filter(Boolean).join(' '),
        ].filter(Boolean).join(', ');

        fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            attributes: {
              '_cc_store_name': store.name || '',
              '_cc_store_address': address,
            },
          }),
        }).catch(() => {});
      }

      _clearCartAttributes() {
        fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            attributes: {
              '_cc_store_name': '',
              '_cc_store_address': '',
            },
          }),
        }).catch(() => {});
      }

      showConfirmation(store) {
        const { confirmation, confirmationStore } = this.refs;
        if (!confirmation || !confirmationStore) {
          this.closePanel();
          return;
        }

        this.classList.add('is-dialog');

        confirmationStore.textContent = store.name.replace(/^Zippy\s*/i, '');
        confirmation.hidden = false;

        const progress = confirmation.querySelector('.cc-store-selector-confirmation__progress');
        if (progress) {
          const clone = progress.cloneNode(true);
          progress.replaceWith(clone);
        }

        if (this.autoCloseTimer) {
          window.clearTimeout(this.autoCloseTimer);
        }

        this.autoCloseTimer = window.setTimeout(() => {
          this.hideConfirmation();
        }, StoreSelector.AUTO_CLOSE_DELAY);
      }

      hideConfirmation() {
        if (this.autoCloseTimer) {
          window.clearTimeout(this.autoCloseTimer);
          this.autoCloseTimer = null;
        }

        if (this.refs.confirmation) this.refs.confirmation.hidden = true;

        this.classList.remove('is-open', 'is-dialog');
        document.body.classList.remove('cc-store-selector-open');
      }

      updateTriggers() {
        const selectedStore = this.getSelectedStore();
        const shortName = selectedStore ? selectedStore.name.replace(/^Zippy\s*/i, '') : '';
        const labelText = shortName || this.i18n.trigger_label;
        const eyebrowText = selectedStore ? this.i18n.eyebrow_selected : this.i18n.eyebrow;

        this.triggerLabels.forEach((node) => {
          const isPlp = node.closest('.cc-store-selector-trigger--plp');
          if (isPlp) {
            node.textContent = shortName || this.i18n.plp_trigger_label;
          } else {
            node.textContent = labelText;
          }
            node.classList.toggle('cc-trigger-label--selected', Boolean(selectedStore));
        });

        this.triggerEyebrows.forEach((node) => {
          node.textContent = eyebrowText;
        });

        this.triggerChangeLinks.forEach((node) => {
          node.classList.toggle('hidden', !selectedStore);
        });

        this.triggerRemoveLinks.forEach((node) => {
          node.classList.toggle('hidden', !selectedStore);
        });

        this.triggerStates.forEach((node) => {
          node.dataset.ccStoreSelectorState = selectedStore ? 'selected' : 'empty';
        });
      }

      computeNearbyGids(selectedStore) {
        const hasRadius = this.radiusKm > 0;
        const lat = Number(selectedStore.latitude);
        const lng = Number(selectedStore.longitude);
        const hasCoords = Number.isFinite(lat) && Number.isFinite(lng);

        const gids = [];
        for (const store of this.stores) {
          if (store.location_id === selectedStore.location_id) continue;
          if (!store.location_id) continue;
          // Warehouse is always handled separately — skip it here
          if (this._isWarehouse(store)) continue;

          // No radius configured — include all stores
          if (!hasRadius) {
            gids.push(store.location_id);
            continue;
          }
          

          // Radius configured — filter by distance
          if (!hasCoords) continue;
          const sLat = Number(store.latitude);
          const sLng = Number(store.longitude);
          if (!Number.isFinite(sLat) || !Number.isFinite(sLng)) continue;
          if (haversine(lat, lng, sLat, sLng) <= this.radiusKm) {
            gids.push(store.location_id);
          }
        }
        return gids;
      }

      getSelectedStore() {
        return this.stores.find((store) => store.location_id === this.selectedStoreId) || null;
      }

      _isWarehouse(store) {
        return store.isWarehouse || (this.warehouseGid && store.location_id === this.warehouseGid);
      }
    },
  );
}
